import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  ButtonGroup,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Close as CloseIcon, CheckCircleOutline, ExpandMore as ExpandMoreIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';
import {
  SandboxProvider,
  SandboxCodeEditor,
  SandboxPreview,
  useSandboxContext,
} from '../../Sandbox';
import type { CodeWidgetConfig } from '@trinity/chat/types/dashboard-config';
import type { FetchState } from './ChartConfigSidebar';
import { AgentIntegrationInfoDialog } from './ChartConfigSidebar';

interface AgentConfig {
  agentId: number;
  setAgentId: (v: number) => void;
  agentIdError: string;
  extensionPath: string;
  setExtensionPath: (v: string) => void;
  extensionPathError: string;
  fetchState: FetchState;
  fetchError: string;
  validateAgentId: (v: number) => boolean;
  validateExtensionPath: (v: string) => boolean;
  handleFetchPreview: () => void;
  /** Ref that the modal writes to — when the agent fetch returns code data, this callback populates /data.js */
  onDataFetchedRef: React.MutableRefObject<((data: Record<string, unknown>) => void) | null>;
  agents: { id: number; name: string }[];
}

interface CodeEditorModalProps {
  open: boolean;
  config: CodeWidgetConfig;
  /** Current data (static content or agent data model placeholder) */
  staticData: Record<string, unknown>;
  /** Current data source type */
  sourceType: 'static' | 'agent';
  /** Agent configuration — passed through from useAgentState in the sidebar */
  agentConfig: AgentConfig;
  /**
   * Called on save with updated title, JSX, parsed data, and source type.
   * - Static: data is the actual content stored and injected at render time.
   * - Agent: data defines the expected shape the agent should return (dataModel).
   */
  onSave: (title: string, jsx: string, data: Record<string, unknown>, sourceType: 'static' | 'agent') => void;
  onClose: () => void;
}

/** Serializes a data object into the /data.js virtual file content */
function serializeDataFile(data: Record<string, unknown>): string {
  let serialized = '{}';
  try {
    serialized = JSON.stringify(data, null, 2);
  } catch {
    /* circular refs */
  }
  return `export const data = ${serialized};`;
}

/** Parses the /data.js file content back into a plain object.
 *  Inverse of serializeDataFile — strips the `export const data = ` prefix
 *  and trailing semicolon, then JSON.parses the remainder. */
function parseDataFile(code: string): Record<string, unknown> {
  const prefix = 'export const data = ';
  const trimmed = code.trim();
  const start = trimmed.indexOf(prefix);
  if (start === -1) return {};
  let json = trimmed.slice(start + prefix.length).trim();
  if (json.endsWith(';')) json = json.slice(0, -1).trim();
  try {
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

// ─── Dense chrome sizing constants ────────────────────────────────────────────
// Intentionally smaller than MUI caption (0.75rem) for compact toolbar UI.
const DENSE_FONT_SIZE = '0.65rem';
const DENSE_FONT_SIZE_MD = '0.7rem';
const DENSE_BTN_MIN_HEIGHT = 24;
const DENSE_INPUT_HEIGHT = 40;

// Stable references to avoid recreating objects on every render, which would reset Sandpack
const VISIBLE_FILES = ['/App.js', '/data.js'];
const EXTERNAL_RESOURCES = ['https://cdn.tailwindcss.com'];
const SANDBOX_STYLE: React.CSSProperties = { display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' };

/**
 * Full-screen code editor modal for the code widget.
 *
 * Layout:
 * - Top bar: close, title, Static/Agent toggle, save
 * - When Agent: compact config row for agent ID + extension path + fetch preview
 * - Main area: code editor (left, with /App.js + /data.js tabs) + live preview (right)
 *
 * /data.js is always editable:
 * - Static mode: this IS the data stored and injected into the component
 * - Agent mode: this defines the expected response shape (dataModel) — at runtime
 *   the agent overwrites it with real data
 */
export function CodeEditorModal({
  open,
  config,
  staticData,
  sourceType: initialSourceType,
  agentConfig,
  onSave,
  onClose,
}: CodeEditorModalProps): React.ReactElement | null {
  // Use state for title so the controlled TextField re-renders on keystrokes.
  // Also mirror into a ref so handleSave always reads the latest value without
  // depending on state (avoids stale closure if Save is clicked before re-render).
  const [pendingTitle, setPendingTitle] = useState(config.title);
  const pendingTitleRef = useRef(config.title);
  // Use refs (not state) for file contents — they're only read at save time.
  // Using state would cause re-renders on every keystroke, creating a new `files`
  // object for SandboxProvider, which resets Sandpack (killing focus + active tab).
  const pendingJsxRef = useRef(config.jsx);
  const pendingDataCodeRef = useRef(serializeDataFile(staticData));
  const sourceTypeRef = useRef(initialSourceType);

  // Stable files object for SandpackProvider — only recreated when the modal
  // re-opens, so Sandpack doesn't reset on parent re-renders.
  const [stableFiles, setStableFiles] = useState(() => ({
    '/App.js': config.jsx,
    '/data.js': serializeDataFile(staticData),
  }));

  // Track external data injections (agent fetch) — this is the only case where
  // we need to imperatively push new content into Sandpack after mount.
  const [externalDataUpdate, setExternalDataUpdate] = useState<string | null>(null);

  // Reset local state when the modal opens with new config
  const prevOpen = useRef(open);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setPendingTitle(config.title);
      pendingTitleRef.current = config.title;
      pendingJsxRef.current = config.jsx;
      const dataCode = serializeDataFile(staticData);
      pendingDataCodeRef.current = dataCode;
      sourceTypeRef.current = initialSourceType;
      setStableFiles({ '/App.js': config.jsx, '/data.js': dataCode });
    }
    prevOpen.current = open;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps include config fields so the
  // effect captures fresh values on the open transition, but the guard (open && !prevOpen) means
  // it only executes when transitioning from closed → open.
  }, [open, config.title, config.jsx, staticData, initialSourceType]);

  // Wire up the agent fetch callback to overwrite /data.js when preview data arrives
  useEffect(() => {
    agentConfig.onDataFetchedRef.current = (fetchedData: Record<string, unknown>) => {
      const code = serializeDataFile(fetchedData);
      pendingDataCodeRef.current = code;
      setExternalDataUpdate(code);
    };
    return () => { agentConfig.onDataFetchedRef.current = null; };
  }, [agentConfig.onDataFetchedRef]);

  const handleSave = useCallback(() => {
    const parsedData = parseDataFile(pendingDataCodeRef.current);
    onSave(pendingTitleRef.current, pendingJsxRef.current, parsedData, sourceTypeRef.current);
    onClose();
  }, [onSave, onClose]);

  if (!open) return null;

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      {/* ── Header: close, editable title, save ── */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          <IconButton edge="start" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
          <Typography variant="subtitle2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Code Widget —
          </Typography>
          <TextField
            variant="standard"
            placeholder="Untitled widget"
            value={pendingTitle}
            onChange={e => { pendingTitleRef.current = e.target.value; setPendingTitle(e.target.value); }}
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.95rem', fontWeight: 500 } }}
            sx={{ flex: 1, maxWidth: 300 }}
          />
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" size="small" onClick={handleSave} disableElevation>
            Save
          </Button>
        </Toolbar>
      </AppBar>

      {/* ── Main area: editor (left) + preview (right) ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <SandboxProvider
        files={stableFiles}
        visibleFiles={VISIBLE_FILES}
        externalResources={EXTERNAL_RESOURCES}
        style={SANDBOX_STYLE}
      >
        {/* Editor + preview row — takes all available space */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* ── Left: code editor with file tabs ── */}
          <Box
            sx={{
              width: '50%',
              display: 'flex',
              flexDirection: 'column',
              borderRight: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <SandboxCodeEditor style={{ height: '100%' }} />
            </Box>
          </Box>

          {/* ── Right: live preview (full height) ── */}
          <Box sx={{ width: '50%' }}>
            <SandboxPreview showRefreshButton style={{ height: '100%' }} />
          </Box>
        </Box>

        {/* Data source panel — pinned to bottom, only visible when /data.js is active */}
        <DataSourcePanel
          sourceTypeRef={sourceTypeRef}
          agentConfig={agentConfig}
        />

        <SandpackFileSyncer
          jsxRef={pendingJsxRef}
          dataCodeRef={pendingDataCodeRef}
        />
        <ExternalDataInjector externalDataUpdate={externalDataUpdate} />
      </SandboxProvider>
      </Box>
    </Dialog>
  );
}

/**
 * Syncs Sandpack file state into parent refs on every change.
 * Uses refs (not callbacks that set state) to avoid re-rendering the parent
 * on every keystroke — the parent only reads these at save time.
 */
function SandpackFileSyncer({
  jsxRef,
  dataCodeRef,
}: {
  jsxRef: React.MutableRefObject<string>;
  dataCodeRef: React.MutableRefObject<string>;
}): null {
  const sandbox = useSandboxContext();
  const jsx = sandbox.getFileCode('/App.js');
  const dataCode = sandbox.getFileCode('/data.js');

  useEffect(() => {
    jsxRef.current = jsx;
  }, [jsx, jsxRef]);

  useEffect(() => {
    dataCodeRef.current = dataCode;
  }, [dataCode, dataCodeRef]);

  return null;
}

/**
 * Data source panel — sits below the code editor, only visible when /data.js
 * is the active file. Vertical layout with clear sections.
 */
function DataSourcePanel({
  sourceTypeRef,
  agentConfig,
}: {
  sourceTypeRef: React.MutableRefObject<'static' | 'agent'>;
  agentConfig: AgentConfig;
}): React.ReactElement | null {
  const sandbox = useSandboxContext();
  const [infoOpen, setInfoOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(sourceTypeRef.current === 'agent');
  // Local state so the panel re-renders on toggle, synced to the parent ref
  const [sourceType, setSourceTypeLocal] = useState(sourceTypeRef.current);
  const setSourceType = useCallback((v: 'static' | 'agent') => {
    sourceTypeRef.current = v;
    setSourceTypeLocal(v);
    if (v === 'agent') setTooltipOpen(true);
  }, [sourceTypeRef]);

  useEffect(() => {
    if (!tooltipOpen) return;
    const timer = setTimeout(() => setTooltipOpen(false), 3000);
    return () => clearTimeout(timer);
  }, [tooltipOpen]);

  if (sandbox.activeFile !== '/data.js') return null;

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        px: 1.5,
        py: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        flexShrink: 0,
      }}
    >
      <AgentIntegrationInfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} showDataModelInfo />

      {/* Row 1: label + toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: DENSE_FONT_SIZE }}>
          Data Source
        </Typography>
        <ButtonGroup size="small">
          <Button
            variant={sourceType === 'static' ? 'contained' : 'outlined'}
            onClick={() => setSourceType('static')}
            disableElevation
            sx={{ textTransform: 'none', fontSize: DENSE_FONT_SIZE_MD, py: 0, px: 1, minHeight: DENSE_BTN_MIN_HEIGHT }}
          >
            Static
          </Button>
          <Button
            variant={sourceType === 'agent' ? 'contained' : 'outlined'}
            onClick={() => setSourceType('agent')}
            disableElevation
            sx={{ textTransform: 'none', fontSize: DENSE_FONT_SIZE_MD, py: 0, px: 1, minHeight: DENSE_BTN_MIN_HEIGHT }}
          >
            Agent
          </Button>
        </ButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: DENSE_FONT_SIZE, ml: 'auto' }}>
          {sourceType === 'static' ? 'Stored with widget' : 'Overwritten by agent at runtime'}
        </Typography>
        {sourceType === 'agent' && (
          <Tooltip
            title="Click for agent integration guide"
            open={tooltipOpen}
            onClose={() => setTooltipOpen(false)}
            onOpen={() => setTooltipOpen(true)}
            arrow
          >
            <IconButton size="small" onClick={() => setInfoOpen(true)} sx={{ p: 0.25, backgroundColor: '#e5f5fc', '&:hover': { backgroundColor: '#d0edf8' } }}>
              <InfoIcon sx={{ fontSize: 16, color: '#28aae1' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Row 2 (agent only): agent + path + fetch — all on one line */}
      {sourceType === 'agent' && (<>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 140, flex: 1 }} error={!!agentConfig.agentIdError}>
            <InputLabel id="code-agent-select">Agent</InputLabel>
            <Select
              labelId="code-agent-select"
              label="Agent"
              value={agentConfig.agentId || ''}
              onChange={e => {
                const v = Number(e.target.value);
                agentConfig.setAgentId(v);
                if (agentConfig.agentIdError) agentConfig.validateAgentId(v);
              }}
              onBlur={() => agentConfig.validateAgentId(agentConfig.agentId)}
            >
              {agentConfig.agents.length === 0 && (
                <MenuItem disabled value=""><em>No agents</em></MenuItem>
              )}
              {agentConfig.agents.map(a => (
                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Extension Path"
            size="small"
            placeholder="/api/report"
            value={agentConfig.extensionPath}
            error={!!agentConfig.extensionPathError}
            onChange={e => {
              agentConfig.setExtensionPath(e.target.value);
              if (agentConfig.extensionPathError) agentConfig.validateExtensionPath(e.target.value);
            }}
            onBlur={e => agentConfig.validateExtensionPath(e.target.value)}
            sx={{ flex: 1, minWidth: 120, '& .MuiInputBase-root': { height: DENSE_INPUT_HEIGHT } }}
          />

          <Button
            size="small"
            variant="outlined"
            onClick={agentConfig.handleFetchPreview}
            disabled={agentConfig.fetchState === 'loading'}
            startIcon={agentConfig.fetchState === 'loading' ? <CircularProgress size={14} /> : undefined}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', minHeight: DENSE_INPUT_HEIGHT }}
          >
            {agentConfig.fetchState === 'loading' ? 'Fetching…' : 'Fetch'}
          </Button>

          {agentConfig.fetchState === 'success' && (
            <CheckCircleOutline sx={{ fontSize: 18 }} color="success" />
          )}

          {agentConfig.fetchState === 'error' && (
            <Typography variant="caption" color="error" noWrap sx={{ fontSize: DENSE_FONT_SIZE, maxWidth: 100 }}>
              Failed
            </Typography>
          )}

        </Box>
      </>)}
    </Box>
  );
}

/**
 * Imperatively updates Sandpack's /data.js when an external source (agent fetch)
 * provides new data. Only fires on explicit external updates — not on user keystrokes
 * (which are handled internally by Sandpack).
 */
function ExternalDataInjector({ externalDataUpdate }: { externalDataUpdate: string | null }): null {
  const sandbox = useSandboxContext();
  const lastApplied = useRef<string | null>(null);

  useEffect(() => {
    if (externalDataUpdate === null || externalDataUpdate === lastApplied.current) return;
    lastApplied.current = externalDataUpdate;
    sandbox.updateFile('/data.js', externalDataUpdate);
  }, [externalDataUpdate, sandbox]);

  return null;
}
